
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, BarChart3, Info, Brain } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from 'react-i18next';

interface TrendData {
  direction: 'up' | 'down' | 'neutral';
  strength: number;
}

interface OverallAssessment {
  summary: string;
  recommendation: string;
  riskLevel: 'Thấp' | 'Trung bình' | 'Cao';
  confidence: number;
  keyInsights: string[];
  tradingStrategy: string;
}

interface TrendsAnalysisCardProps {
  trends: {
    short: TrendData;
    medium: TrendData;
    long: TrendData;
    overall_assessment?: OverallAssessment;
  };
}

const getTrendLabel = (timeframe: string, t: (key: string) => string): string => {
  switch(timeframe) {
    case 'short': return t('market_analysis.short_term');
    case 'medium': return t('market_analysis.medium_term');
    case 'long': return t('market_analysis.long_term');
    default: return timeframe;
  }
};

export const TrendsAnalysisCard = ({ trends }: TrendsAnalysisCardProps) => {
  const { t } = useTranslation();
  const getTrendIcon = (direction: 'up' | 'down' | 'neutral') => {
    switch(direction) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <Minus className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getTrendColorScheme = (direction: 'up' | 'down' | 'neutral') => {
    switch(direction) {
      case 'up': return { 
        gradient: 'from-green-500 to-green-400',
        bg: 'bg-green-500/10 border-green-500/20',
        text: 'text-green-600'
      };
      case 'down': return { 
        gradient: 'from-red-500 to-red-400',
        bg: 'bg-red-500/10 border-red-500/20',
        text: 'text-red-600'
      };
      default: return { 
        gradient: 'from-gray-500 to-gray-400',
        bg: 'bg-gray-500/10 border-gray-500/20',
        text: 'text-gray-600'
      };
    }
  };

  const getRiskLevelColor = (level: string) => {
    switch(level) {
      case 'Thấp': return 'text-green-600 bg-green-500/10';
      case 'Cao': return 'text-red-600 bg-red-500/10';
      default: return 'text-yellow-600 bg-yellow-500/10';
    }
  };

  // Extract only the trend data (exclude overall_assessment)
  const trendEntries = Object.entries(trends).filter(([key]) => key !== 'overall_assessment') as Array<[string, TrendData]>;

  return (
    <TooltipProvider>
      <Card className="relative overflow-hidden bg-gradient-to-br from-background via-background to-blue-500/5 border border-border/50 backdrop-blur-sm hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 hover:-translate-y-1 group card-shine soft-glow">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(59,130,246,0.08),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute inset-0 shimmer-slide opacity-0 group-hover:opacity-30 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        
        <CardHeader className="relative z-10 pb-4">
          <CardTitle className="text-foreground group-hover:text-blue-600 transition-colors duration-300 flex items-center gap-2">
            <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500 group-hover:bg-blue-500/20 transition-all duration-300 floating-action">
              <BarChart3 className="h-5 w-5 icon-spin-hover" />
            </div>
            <span>{t('market_analysis.multi_timeframe_analysis')}</span>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Phân tích xu hướng giá theo các khung thời gian khác nhau</p>
              </TooltipContent>
            </Tooltip>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="relative z-10 space-y-6">
          {/* Compact Trend Analysis - 3 cards in a row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {trendEntries.map(([timeframe, trend], index) => {
              const label = getTrendLabel(timeframe, t);
              const colors = getTrendColorScheme(trend.direction);
              
              return (
                <div 
                  key={timeframe} 
                  className={`p-3 rounded-lg transition-all duration-300 backdrop-blur-sm border ${colors.bg} hover:shadow-sm card-hover-effect gradient-animate`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-md bg-background/50 backdrop-blur-sm">
                        {getTrendIcon(trend.direction)}
                      </div>
                      <span className="text-sm font-medium text-foreground">{label}</span>
                    </div>
                  </div>
                  
                  {/* Large percentage display */}
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${colors.text} mb-1`}>
                      {trend.strength}%
                    </div>
                    <div className="w-full bg-muted/50 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-500 bg-gradient-to-r ${colors.gradient}`}
                        style={{ width: `${trend.strength}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* AI-Generated Overall Assessment - Full Width */}
          {trends.overall_assessment && (
            <div className="p-5 bg-gradient-to-r from-purple-500/10 via-purple-500/5 to-transparent rounded-xl border border-purple-500/20">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="h-6 w-6 text-purple-500" />
                <h4 className="text-lg font-semibold text-foreground">{t('market_analysis.ai_comprehensive_assessment')}</h4>
                <div className="flex gap-2 ml-auto">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelColor(trends.overall_assessment.riskLevel)}`}>
                    {t('market_analysis.risk_level')}: {trends.overall_assessment.riskLevel}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-500/10 text-blue-600">
                    {t('market_analysis.confidence')}: {trends.overall_assessment.confidence}%
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-purple-600">{t('market_analysis.overview')}: </span>
                  <span className="text-foreground">{trends.overall_assessment.summary}</span>
                </div>
                
                <div>
                  <span className="font-medium text-purple-600">{t('market_analysis.recommendation')}: </span>
                  <span className="text-foreground">{trends.overall_assessment.recommendation}</span>
                </div>

                <div className="md:col-span-2">
                  <span className="font-medium text-purple-600">{t('market_analysis.strategy')}: </span>
                  <span className="text-foreground">{trends.overall_assessment.tradingStrategy}</span>
                </div>

                {trends.overall_assessment.keyInsights && trends.overall_assessment.keyInsights.length > 0 && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-purple-600">{t('market_analysis.key_insights')}:</span>
                    <ul className="mt-2 ml-4 space-y-1">
                      {trends.overall_assessment.keyInsights.map((insight, index) => (
                        <li key={index} className="text-foreground text-sm list-disc">
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
